















import { Component, Prop, Vue } from 'vue-property-decorator';
import { GraphsApi } from '@/api';
import moment from 'moment';
import DatePicker from '@/components/form pickers/DatePicker.vue';
import { DateRange } from '@/models/date-range';
import { LineSeries } from '@amcharts/amcharts5/.internal/charts/xy/series/LineSeries';
import {
	Bullet,
	Circle,
	Color,
	Label,
	Legend,
	percent,
	Root,
	Scrollbar,
	Tooltip,
} from '@amcharts/amcharts5';
import { DateAxis } from '@amcharts/amcharts5/.internal/charts/xy/axes/DateAxis';
import { AxisRenderer } from '@amcharts/amcharts5/.internal/charts/xy/axes/AxisRenderer';
import { ApiUtils } from '@/common/ApiUtils';
import { XYChart } from '@amcharts/amcharts5/.internal/charts/xy/XYChart';
import { ValueAxis } from '@amcharts/amcharts5/.internal/charts/xy/axes/ValueAxis';
import { XYCursor } from '@amcharts/amcharts5/.internal/charts/xy/XYCursor';
import { AxisRendererX } from '@amcharts/amcharts5/.internal/charts/xy/axes/AxisRendererX';
import { AxisRendererY } from '@amcharts/amcharts5/.internal/charts/xy/axes/AxisRendererY';
import { filterArrayByTsAfter2010 } from '@/common/DateFilterUtil';

@Component({
	components: { DatePicker },
})
export default class EntityChargingBehaviourChart extends Vue {
	@Prop({ required: true })
	public entityId!: number;

	@Prop({ required: true })
	public entityType!: string;

	public datePicker?: DatePicker | null = null;

	public errorMessage: string | null = null;
	public loading: boolean = true;
	public graphData: any[] | null = null;

	private graphApi: GraphsApi = new GraphsApi();

	private batterySeries: LineSeries | null = null;
	private chargeStateSeries: LineSeries | null = null;

	private xAxis: DateAxis<AxisRenderer> | null = null;

	private startDate: moment.Moment = moment()
		.startOf('day')
		.subtract(5, 'days');
	private endDate: moment.Moment = moment()
		.startOf('day')
		.add(1, 'days');

	public async created(): Promise<void> {
		if (this.entityId == null || this.entityType == null) {
			this.errorMessage = 'Invalid entity provided';
			return;
		}
		await this.fetchGraphData(this.entityId, this.entityType);
		this.createChart(this.graphData!);

		this.datePicker = <DatePicker>this.$refs.datePicker;
		this.datePicker?.onDateRangeChanged(<DateRange>{
			start: this.startDate.format('DD-MM-YYYY'),
			end: this.endDate.format('DD-MM-YYYY'),
		});
	}

	public async onDateRangeChanged(newDateRange: Omit<DateRange, 'shortcut'>): Promise<void> {
		this.startDate = moment(newDateRange.start, 'DD-MM-YYYY');
		this.endDate = moment(newDateRange.end, 'DD-MM-YYYY');

		await this.fetchGraphData(this.entityId, this.entityType);

		this.batterySeries?.data.setAll(this.formatGraphData(this.graphData!, 'batt_mV'));
		this.chargeStateSeries?.data.setAll(
			this.formatGraphData(this.graphData!, 'batt_mV', 'chrg_state'),
		);

		this.xAxis?.setAll({
			min: this.startDate.valueOf(),
			max: Math.min(this.endDate.valueOf(), Date.now()),
		});
	}

	private async fetchGraphData(entityId: number, entityType: string): Promise<void> {
		try {
			const graphDataResponse = await this.graphApi.graph(
				entityType,
				entityId,
				this.startDate.format('X'),
				this.endDate.format('X'),
				'power',
			);
			this.graphData =
				(graphDataResponse.data.data?.power ?? []).map((x: any) => {
					const chrg_state = x?.value?.chrg_state;
					switch (chrg_state) {
						case 'c/x_term':
							x.value.chrg_state = 'Full';
							break;
						case 'cc_cv_chrg':
							x.value.chrg_state = 'Charging';
							break;
						case 'chrg_susp':
							x.value.chrg_state = 'No Solar';
							break;
					}
					return { ...x.value, timestamp: x.timestamp };
				}) ?? [];
			this.graphData = filterArrayByTsAfter2010(this.graphData);
		} catch (e) {
			const errorResponse = ApiUtils.handleApiError(e, 'Failed to load graph data');
			this.errorMessage = errorResponse.errorMessage;
			if (this.errorMessage === 'Resource not found') this.errorMessage = null;
			this.graphData = [];
		} finally {
			this.loading = false;
		}
	}

	private createChart(graphData: any[]): void {
		const root: Root = Root.new('batteryChart');
		const chart: XYChart = XYChart.new(root, {});

		const {
			cursor,
			xAxis,
			batteryYAxis,
			batterySeries,
			chargeStateSeries,
			legend,
		} = this.createChartFeatures(root);
		this.batterySeries = batterySeries;
		this.chargeStateSeries = chargeStateSeries;

		this.xAxis = xAxis;
		chart.set('cursor', cursor);
		chart.xAxes.push(xAxis);

		chart.yAxes.push(batteryYAxis);

		chart.series.push(batterySeries);
		chart.series.push(chargeStateSeries);

		let scrollbarX = Scrollbar.new(root, { orientation: 'horizontal' });
		chart.set('scrollbarX', scrollbarX);

		chart.bottomAxesContainer.children.push(legend);

		batterySeries.data.setAll(this.formatGraphData(graphData, 'batt_mV'));
		chargeStateSeries.data.setAll(this.formatGraphData(graphData, 'batt_mV', 'chrg_state'));

		legend.data.setAll(chart.series.values);
		root.container.children.push(chart);

		batterySeries.appear(1000);
		chargeStateSeries.appear(1000);

		chart.appear(1000, 100);
	}

	private formatGraphData(
		graphData: any[],
		field: string,
		labelField?: string,
		min?: number,
		max?: number,
	): {
		value: number | undefined;
		date: number;
	}[] {
		return <{ value: number | undefined; date: number }[]>graphData
				?.filter(
					(item: any) => min == null || max == null || (item[field] >= min && item[field] <= max),
				)
				.map((item: any, index: number) => {
					let out: any = {
						value: item[field],
						date: item.timestamp,
					};

					if (labelField) {
						const diffToPrev = index === 0 || graphData[index - 1][labelField] !== item[labelField];
						if (diffToPrev) out.label = item[labelField];
						else return null;
					}
					return out;
				})
				?.filter(x => x != null) ?? [];
	}

	private createChartFeatures(
		root: Root,
	): {
		xAxis: DateAxis<AxisRenderer>;
		batteryYAxis: ValueAxis<AxisRenderer>;
		cursor: XYCursor;
		batterySeries: LineSeries;
		chargeStateSeries: LineSeries;
		legend: Legend;
	} {
		const xAxis: DateAxis<AxisRenderer> = DateAxis.new(root, {
			baseInterval: { timeUnit: 'minute', count: 15 },
			renderer: AxisRendererX.new(root, {}),
			tooltip: Tooltip.new(root, {}),
			min: this.startDate.toDate().getTime(),
			max: Math.min(this.endDate.toDate().getTime(), Date.now()),
		});

		const batteryYAxis: ValueAxis<AxisRenderer> = ValueAxis.new(root, {
			renderer: AxisRendererY.new(root, {
				opposite: true,
			}),
			min: 2500,
			max: 4000,
		});

		const cursor: XYCursor = XYCursor.new(root, { behavior: 'zoomX' });

		const batterySeries: LineSeries = LineSeries.new(root, {
			name: 'Battery Voltage (mV)',
			xAxis,
			yAxis: batteryYAxis,
			tooltip: Tooltip.new(root, {
				labelText: 'Battery {valueY}mV',
			}),
			showTooltipOn: 'always',
			valueXField: 'date',
			valueYField: 'value',
			connect: true,
			snapTooltip: true,
			fill: Color.fromHex(0x769edf),
			stroke: Color.fromHex(0x769edf),
		});
		batterySeries.strokes.template.setAll({
			strokeWidth: 2,
		});

		const chargeStateSeries = LineSeries.new(root, {
			name: 'Charge State',
			xAxis,
			yAxis: batteryYAxis,
			showTooltipOn: 'always',
			valueXField: 'date',
			valueYField: 'value',
			connect: false,
			snapTooltip: true,
			fill: Color.fromHex(0x769edf),
			stroke: Color.fromHex(0x769edf),
		});

		chargeStateSeries.bullets.push(r => {
			return Bullet.new(r, {
				locationX: 0.5,
				locationY: 0.5,
				sprite: Circle.new(r, {
					radius: 3,
					fill: Color.fromRGB(255, 255, 255),
					stroke: Color.fromRGB(170, 170, 170),
					strokeWidth: 2,
				}),
			});
		});
		chargeStateSeries.bullets.push(r => {
			return Bullet.new(r, {
				sprite: Label.new(r, {
					text: '{label}',
					centerX: percent(50),
					centerY: percent(100),
					populateText: true,
					fontFamily: 'sofia-pro-soft',
					fontSize: 11,
					fontWeight: '400',
				}),
			});
		});

		const batteryYAxisRenderer = batteryYAxis.get('renderer');
		batteryYAxisRenderer.labels.template.setAll({
			fontFamily: 'sofia-pro-soft',
			fill: batterySeries.get('fill'),
		});

		const legend: Legend = Legend.new(root, {
			x: percent(50),
			centerX: percent(50),
		});
		return {
			xAxis,
			batteryYAxis,
			cursor,
			batterySeries,
			chargeStateSeries,
			legend,
		};
	}
}
